import { useEffect, useSyncExternalStore } from 'react'
import { useAuth } from 'hooks/auth'
import { getAuthToken, MASQUERADE_CHANGED_EVENT } from 'helpers/auth'
import { AppSocket, reconnectIfCredentialChanged } from 'helpers/socketClient'

// The credential lives in localStorage. Masquerade start/stop announce
// themselves with MASQUERADE_CHANGED_EVENT; another tab's sign-in, refresh or
// masquerade arrives as a `storage` event.
const subscribe = (onChange: () => void): (() => void) => {
  window.addEventListener(MASQUERADE_CHANGED_EVENT, onChange)
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(MASQUERADE_CHANGED_EVENT, onChange)
    window.removeEventListener('storage', onChange)
  }
}

const currentCredential = (): string | null => getAuthToken() ?? null

/**
 * Keep the socket's handshake credential in step with the session. The
 * websocket server authorises room joins with the credential the connection
 * was opened with, so a long-lived tab reconnects whenever the credential
 * that `socketAuth` would send changes (token refresh, sign-in, sign-out,
 * masquerade start/stop) and its rooms are re-joined as the current user.
 */
export const useSocketCredentialSync = (socket: AppSocket): void => {
  // Sign-in, refresh and sign-out write the credential inside auth state
  // updates, with no event; subscribing to the auth context re-renders this
  // hook so the snapshot below is read again.
  useAuth()
  const credential = useSyncExternalStore(subscribe, currentCredential)

  useEffect(() => {
    reconnectIfCredentialChanged(socket)
  }, [socket, credential])
}
